import React from 'react'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  IconButton,
  LinearProgress,
  Stack,
  Typography
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import DownloadIcon from '@mui/icons-material/Download'
import AudioFileIcon from '@mui/icons-material/AudioFile'
import VideoFileIcon from '@mui/icons-material/VideoFile'
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty'
import { FileItem } from '../services/api'

type Props = {
  file: FileItem | null
  onClose: () => void
  onDownload: (file: FileItem) => void
}

export default function FileViewer({ file, onClose, onDownload }: Props) {
  if (!file) return null

  const isCompleted = file.status === 'completed'
  const isProcessing = file.status === 'processing'
  const isFailed = file.status === 'failed'
  const progress = typeof file.progress === 'number' ? file.progress : 50

  const FileIcon = () => {
    const type = file.type?.toLowerCase() || ''
    const title = file.title?.toLowerCase() || ''
    const iconSx = { fontSize: 64 }

    // Verificar por tipo ou extensão no título
    const audioTypes = ['audio', 'mp3', 'wav', 'ogg', 'flac', 'aac', 'm4a', 'wma']
    const videoTypes = ['video', 'mp4', 'avi', 'mov', 'mkv', 'webm', 'wmv', 'flv', 'm4v']

    const isAudio = audioTypes.some(t => type.includes(t) || title.endsWith(`.${t}`))
    const isVideo = videoTypes.some(t => type.includes(t) || title.endsWith(`.${t}`))

    if (isAudio) {
      return <AudioFileIcon sx={{ ...iconSx, color: '#1976d2' }} />
    }
    if (isVideo) {
      return <VideoFileIcon sx={{ ...iconSx, color: '#9c27b0' }} />
    }
    return <InsertDriveFileIcon sx={{ ...iconSx, color: '#757575' }} />
  }

  const formatBytes = (bytes: number) => {
    if (!bytes) return '—'
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), sizes.length - 1)
    const value = bytes / 1024 ** i
    return `${value.toFixed(value >= 10 ? 0 : 1)} ${sizes[i]}`
  }

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '—'
    // O backend retorna timestamp como string numérica (ex: "1769454102923")
    const timestamp = /^\d+$/.test(dateStr) ? parseInt(dateStr, 10) : Date.parse(dateStr)
    if (isNaN(timestamp)) return '—'
    
    return new Date(timestamp).toLocaleString('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const StatusChip = () => {
    if (isCompleted) {
      return (
        <Chip
          icon={<CheckCircleIcon />}
          label="Transcrição concluída"
          color="success"
          size="small"
        />
      )
    }
    if (isFailed) {
      return (
        <Chip
          icon={<ErrorIcon />}
          label="Erro no processamento"
          color="error"
          size="small"
        />
      )
    }
    return (
      <Chip
        icon={<HourglassEmptyIcon />}
        label="Processando..."
        color="warning"
        size="small"
      />
    )
  }

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3 }
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          py: 2,
          borderBottom: '1px solid #e0e0e0'
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Detalhes do arquivo
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={{ py: 3 }}>
        <Stack spacing={3}>
          {/* Ícone e título */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                p: 2,
                bgcolor: '#f5f5f5',
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <FileIcon />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
                title={file.title}
              >
                {file.title}
              </Typography>
              <StatusChip />
            </Box>
          </Box>

          {/* Progress bar para processando */}
          {isProcessing && (
            <Box sx={{ bgcolor: '#fff8e1', p: 2, borderRadius: 2, border: '1px solid #ffe082' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                <CircularProgress size={20} color="warning" />
                <Typography variant="body2" sx={{ fontWeight: 500, color: '#f57c00' }}>
                  {file.message || 'Processando transcrição...'}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={progress}
                color="warning"
                sx={{ height: 8, borderRadius: 4 }}
              />
              <Typography variant="caption" sx={{ color: '#666', mt: 0.5, display: 'block' }}>
                {progress}% concluído • {file.currentSubState || file.currentState || 'Aguarde...'}
              </Typography>
            </Box>
          )}

          {/* Erro */}
          {isFailed && (
            <Box sx={{ bgcolor: '#ffebee', p: 2, borderRadius: 2, border: '1px solid #ffcdd2' }}>
              <Typography variant="body2" sx={{ color: '#c62828' }}>
                {file.message || 'Ocorreu um erro durante o processamento do arquivo.'}
              </Typography>
            </Box>
          )}

          <Divider />

          {/* Informações */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <InfoItem label="Tipo" value={file.type || '—'} />
            <InfoItem label="Tamanho" value={formatBytes(file.bytes)} />
            <InfoItem label="Criado em" value={formatDate(file.createdAt)} />
            <InfoItem label="Atualizado em" value={formatDate(file.completedAt)} />
          </Box>

          {isCompleted && (
            <>
              <Divider />
              <Typography variant="body2" sx={{ color: '#666' }}>
                A transcrição está pronta. Clique em "Baixar" para obter o arquivo JSON com o texto transcrito.
              </Typography>
            </>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #e0e0e0' }}>
        <Button onClick={onClose} color="inherit">
          Fechar
        </Button>
        <Button
          variant="contained"
          startIcon={<DownloadIcon />}
          onClick={() => onDownload(file)}
          disabled={!isCompleted}
        >
          Baixar transcrição
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function InfoItem({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Typography variant="caption" sx={{ color: '#666', textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {value}
      </Typography>
    </Box>
  )
}
